import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, Check, X, Shield, Info } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('aquargin_cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      
      // Load analytics if consented
      if (savedPreferences.analytics) {
        loadGoogleAnalytics();
      }
    }
  }, []);

  const loadGoogleAnalytics = () => {
    if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
      // Google Analytics will be loaded by the GoogleAnalytics component
      window.gtag && window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    setPreferences(allAccepted);
    localStorage.setItem('aquargin_cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('aquargin_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    
    // Load analytics
    loadGoogleAnalytics();
    
    // Track consent acceptance
    if (window.gtag) {
      window.gtag('event', 'cookie_consent', {
        event_category: 'engagement',
        event_label: 'accept_all'
      });
    }
  };

  const handleRejectAll = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    setPreferences(necessaryOnly);
    localStorage.setItem('aquargin_cookie_consent', JSON.stringify(necessaryOnly));
    localStorage.setItem('aquargin_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    
    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    localStorage.setItem('aquargin_cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('aquargin_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    
    // Update analytics consent
    if (preferences.analytics) {
      loadGoogleAnalytics();
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const handleTogglePreference = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Necessary Cookies',
      description: 'Essential for the website to function properly. These cannot be disabled.',
      examples: 'Session management, security, basic functionality',
      required: true
    },
    {
      key: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting information anonymously.',
      examples: 'Google Analytics, page views, user behavior',
      required: false
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display relevant advertisements.',
      examples: 'Ad targeting, conversion tracking, remarketing',
      required: false
    },
    {
      key: 'functional',
      title: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization.',
      examples: 'Language preferences, chat widgets, video players',
      required: false
    }
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && !showSettings && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4"
          >
            <div className="max-w-7xl mx-auto">
              <div className="bg-white border-2 border-gray-200 rounded-lg shadow-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                      <Cookie className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      We value your privacy
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      We use cookies to enhance your browsing experience, analyze site traffic, and provide personalized content. 
                      By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or learn more in our{' '}
                      <a href="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                        Privacy Policy
                      </a>.
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Accept All
                      </button>
                      
                      <button
                        onClick={handleRejectAll}
                        className="border-2 border-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium hover:border-gray-400 transition-colors duration-300"
                      >
                        Reject All
                      </button>
                      
                      <button
                        onClick={() => setShowSettings(true)}
                        className="text-primary-600 hover:text-primary-700 px-6 py-2 rounded-lg font-medium hover:bg-primary-50 transition-colors duration-300 flex items-center"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Customize
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowBanner(false)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors duration-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    Manage your cookie preferences below. You can enable or disable different types of cookies. 
                    Note that disabling certain cookies may impact your experience on our website.
                  </p>
                </div>

                <div className="space-y-6">
                  {cookieTypes.map((type) => (
                    <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{type.title}</h3>
                            {type.required && (
                              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{type.description}</p>
                          <p className="text-gray-500 text-xs">
                            <strong>Examples:</strong> {type.examples}
                          </p>
                        </div>
                        
                        <div className="flex-shrink-0 ml-4">
                          <button
                            onClick={() => handleTogglePreference(type.key)}
                            disabled={type.required}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              preferences[type.key]
                                ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                                : 'bg-gray-200'
                            } ${type.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                preferences[type.key] ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center mt-6 p-4 bg-blue-50 rounded-lg">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mr-3" />
                  <p className="text-blue-800 text-sm">
                    You can change these preferences at any time by clicking the cookie settings link in our footer 
                    or by visiting our Privacy Policy page.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Utility function to check if analytics are allowed
export const isAnalyticsEnabled = () => {
  const consent = localStorage.getItem('aquargin_cookie_consent');
  if (!consent) return false;
  
  const preferences = JSON.parse(consent);
  return preferences.analytics === true;
};

// Utility function to show cookie settings
export const showCookieSettings = () => {
  window.dispatchEvent(new CustomEvent('show-cookie-settings'));
};

export default CookieConsent;
